(() => {
  const STORAGE_KEY = 'pubboCloudTransition';
  const MAX_PROGRESS = 1.48;
  const DURATION_OUT = 2600;
  const DURATION_FADE = 420;
  const FETCH_TIMEOUT = 4000;
  const DEFAULT_COLOR = '#f08300';

  const VERT = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main() {
      vUv = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }
  `;

  const FRAG = `
    precision mediump float;
    varying vec2 vUv;
    uniform vec2 uMouse;
    uniform vec2 uResolution;
    uniform float uProgress;
    uniform float uTime;
    uniform vec3 uColor;

    void main() {
      float aspect = uResolution.x / max(uResolution.y, 1.0);
      vec2 uv = vUv;
      vec2 mouse = uMouse;
      uv.x *= aspect;
      mouse.x *= aspect;

      float p = max(uProgress, 0.0);
      float t = clamp(p / 1.48, 0.0, 1.0);
      float grow = 1.0 - (1.0 - t) * (1.0 - t);
      float need = max(
        max(distance(mouse, vec2(0.0, 0.0)), distance(mouse, vec2(aspect, 0.0))),
        max(distance(mouse, vec2(0.0, 1.0)), distance(mouse, vec2(aspect, 1.0)))
      );
      float radius = max(need * 1.08 * grow, 0.001);

      vec2 dir = uv - mouse;
      float dist = length(dir);
      float ang = atan(dir.y, dir.x);
      vec2 warp = vec2(
        sin(uv.y * 11.0 + uTime * 2.1),
        sin(uv.x * 9.0 - uTime * 1.8)
      ) * (0.016 * radius);
      dist = length(dir + warp);
      float wave = 1.0
        + 0.07 * sin(ang * 5.0 - uTime * 3.2)
        + 0.04 * sin(ang * 8.0 + uTime * 2.4)
        + 0.022 * sin(ang * 3.0 - uTime * 1.5);
      float edge = radius * wave;

      float pixel = 1.0 / max(uResolution.y, 1.0);
      float feather = pixel * 12.0;
      float core = 1.0 - smoothstep(edge - feather, edge + feather, dist);

      vec3 ringColor = mix(uColor, vec3(1.0), 0.42);
      float rings = 0.0;
      for (int i = 0; i < 3; i++) {
        float lag = float(i + 1) * 0.13;
        float rt = clamp((t - lag) / max(1.0 - lag, 0.001), 0.0, 1.0);
        float rr = need * 1.08 * (1.0 - (1.0 - rt) * (1.0 - rt)) * (
          1.0
          + 0.07 * sin(ang * 5.0 - (uTime + float(i) * 0.4) * 3.2)
          + 0.04 * sin(ang * 8.0 + (uTime + float(i) * 0.4) * 2.4)
        );
        float stroke = pixel * 3.0;
        float ring = smoothstep(rr - stroke - feather * 0.5, rr, dist)
          * (1.0 - smoothstep(rr, rr + stroke + feather * 0.5, dist));
        rings += ring * 0.42 * sin(rt * 3.14159265);
      }

      float alpha = max(core, rings);
      alpha *= step(0.001, p);
      vec3 color = mix(ringColor, uColor, core);

      gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
    }
  `;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseColor(value) {
    const raw = String(value || DEFAULT_COLOR).trim();
    const rgb = raw.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgb) {
      const alphaMatch = raw.match(
        /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)/i
      );
      const alpha = alphaMatch ? Number(alphaMatch[1]) : 1;
      if (alpha < 0.08) return parseColor(DEFAULT_COLOR);
      return [Number(rgb[1]) / 255, Number(rgb[2]) / 255, Number(rgb[3]) / 255];
    }
    const hex = raw.replace('#', '');
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex.padEnd(6, '0').slice(0, 6);
    const num = parseInt(full, 16);
    if (Number.isNaN(num)) return [0.941, 0.514, 0];
    return [(num >> 16) / 255, ((num >> 8) & 255) / 255, (num & 255) / 255];
  }

  function colorToHex(value) {
    const [r, g, b] = parseColor(value);
    const toHex = (n) =>
      Math.round(n * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  function isUsableColor(value) {
    if (!value) return false;
    const raw = String(value).trim().toLowerCase();
    if (!raw || raw === 'transparent' || raw === 'none') return false;
    if (raw === 'rgba(0, 0, 0, 0)' || raw === 'rgba(0,0,0,0)') return false;
    return true;
  }

  function resolveLinkColor(link, fallback) {
    if (!link) return fallback;
    const css = getComputedStyle(link);
    const candidates = [
      link.getAttribute('data-cloud-color'),
      link.getAttribute('data-color'),
      link.dataset.cloudColor,
      link.dataset.color,
      css.getPropertyValue('--pubbo-cloud-color'),
    ];
    const marked = candidates
      .map((item) => String(item || '').trim())
      .find(isUsableColor);
    if (marked) return marked;
    const bg = css.backgroundColor;
    if (isUsableColor(bg)) {
      const parsed = parseColor(bg);
      const isBlack = parsed[0] < 0.02 && parsed[1] < 0.02 && parsed[2] < 0.02;
      if (!isBlack) return bg;
    }
    const theme = css.getPropertyValue('--twm-btn-bg').trim();
    return isUsableColor(theme) ? theme : fallback;
  }

  function easeInOutSmooth(t) {
    return t * t * (3 - 2 * t);
  }

  function easeOutSine(t) {
    return Math.sin((t * Math.PI) / 2);
  }

  function readStore() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writeStore(data) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      /* ignore quota / private mode */
    }
  }

  function clearStore() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      /* ignore */
    }
  }

  const pageCache = new Map();

  function fetchPage(href) {
    if (pageCache.has(href)) return pageCache.get(href);
    const req = fetch(href, { credentials: 'same-origin' }).then((res) => {
      if (!res.ok) throw new Error('fetch failed');
      return res.text();
    });
    pageCache.set(href, req);
    return req;
  }

  function runScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      if (old.src && document.querySelector(`script[src="${old.src}"]`)) {
        old.remove();
        return;
      }
      const script = document.createElement('script');
      Array.from(old.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      if (!old.src) script.textContent = old.textContent;
      old.replaceWith(script);
    });
  }

  function syncLive2d(doc) {
    const nextRoot = doc.querySelector('#live2d-root');
    const currRoot = document.querySelector('#live2d-root');
    if (currRoot && !nextRoot) {
      if (typeof window.__pubboLive2dTeardown === 'function') {
        window.__pubboLive2dTeardown();
      }
      currRoot.remove();
      return;
    }
    if (nextRoot && !currRoot) {
      const imported = document.importNode(nextRoot, true);
      document.body.appendChild(imported);
      runScripts(imported);
    }
  }

  function getUrlHash(url) {
    try {
      return new URL(url, window.location.href).hash || '';
    } catch (err) {
      const index = String(url).indexOf('#');
      return index >= 0 ? String(url).slice(index) : '';
    }
  }

  function jumpToHash(url) {
    const hash = getUrlHash(url);
    if (!hash || hash === '#') {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    const go = () => {
      const companion = document.querySelector('pubbo-companion');
      if (companion && companion.__pcp && typeof companion.__pcp.applyHash === 'function') {
        companion.__pcp.applyHash(hash);
        return;
      }
      const el = document.getElementById(id);
      if (!el) return;
      const header = document.querySelector('sticky-header, .header');
      const offset = header ? header.getBoundingClientRect().height : 0;
      const top =
        el.getBoundingClientRect().top +
        (window.pageYOffset || document.documentElement.scrollTop || 0) -
        offset -
        8;
      window.scrollTo(0, Math.max(0, top));
    };
    go();
    requestAnimationFrame(go);
    window.setTimeout(go, 80);
    window.setTimeout(go, 280);
    window.setTimeout(go, 700);
  }

  function applyPage(html, href) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextShell = doc.querySelector('.transition-body');
    const currShell = document.querySelector('.transition-body');
    if (!nextShell || !currShell) throw new Error('missing shell');

    document.title = doc.title || document.title;
    const nextBodyClass = doc.body?.getAttribute('class');
    if (nextBodyClass) document.body.className = nextBodyClass;
    document.body.classList.add('loaded');
    document.body.classList.remove('unloading');

    const imported = document.importNode(nextShell, true);
    currShell.replaceWith(imported);
    let nextUrl = href;
    try {
      nextUrl = new URL(href, window.location.href).href;
    } catch (err) {
      nextUrl = href;
    }
    history.pushState({ pubboCloud: true }, '', nextUrl);
    runScripts(imported);
    syncLive2d(doc);
    document.dispatchEvent(new CustomEvent('page:loaded'));
    jumpToHash(nextUrl);
  }

  class CloudTransition {
    constructor(canvas) {
      this.canvas = canvas;
      this.gl = canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
      });
      this.color = parseColor(canvas.dataset.color || DEFAULT_COLOR);
      this.mouse = [0.5, 0.5];
      this.progress = 0;
      this.animating = false;
      this.raf = 0;
      if (this.gl) this.setup();
    }

    setup() {
      const gl = this.gl;
      const program = this.createProgram(VERT, FRAG);
      if (!program) {
        this.gl = null;
        return;
      }
      this.program = program;
      gl.useProgram(program);

      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 3, -1, -1, 3]),
        gl.STATIC_DRAW
      );
      const aPos = gl.getAttribLocation(program, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      this.uniforms = {
        uMouse: gl.getUniformLocation(program, 'uMouse'),
        uResolution: gl.getUniformLocation(program, 'uResolution'),
        uProgress: gl.getUniformLocation(program, 'uProgress'),
        uTime: gl.getUniformLocation(program, 'uTime'),
        uColor: gl.getUniformLocation(program, 'uColor'),
      };

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      this.resize();
      window.addEventListener('resize', () => this.resize(), { passive: true });
    }

    createProgram(vertSrc, fragSrc) {
      const gl = this.gl;
      const vert = this.compile(gl.VERTEX_SHADER, vertSrc);
      const frag = this.compile(gl.FRAGMENT_SHADER, fragSrc);
      if (!vert || !frag) return null;
      const program = gl.createProgram();
      gl.attachShader(program, vert);
      gl.attachShader(program, frag);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
      return program;
    }

    compile(type, source) {
      const gl = this.gl;
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return null;
      return shader;
    }

    resize() {
      if (!this.gl) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.round(window.innerWidth * dpr));
      const height = Math.max(1, Math.round(window.innerHeight * dpr));
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);
      }
    }

    setMouse(clientX, clientY) {
      this.mouse = [
        clientX / Math.max(window.innerWidth, 1),
        1 - clientY / Math.max(window.innerHeight, 1),
      ];
    }

    setColor(hex) {
      this.color = parseColor(hex);
    }

    show() {
      this.canvas.classList.add('is-on');
    }

    hide() {
      this.canvas.classList.remove('is-on');
      this.canvas.style.backgroundColor = '';
      this.canvas.style.opacity = '';
    }

    reset() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.animating = false;
      this.progress = 0;
      this.hide();
      this.draw(0);
    }

    snapOff() {
      this.reset();
    }

    draw(time) {
      const gl = this.gl;
      if (!gl) return;
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      if (this.progress <= 0.001) return;
      gl.useProgram(this.program);
      gl.uniform2f(this.uniforms.uMouse, this.mouse[0], this.mouse[1]);
      gl.uniform2f(
        this.uniforms.uResolution,
        this.canvas.width,
        this.canvas.height
      );
      gl.uniform1f(this.uniforms.uProgress, this.progress);
      gl.uniform1f(this.uniforms.uTime, time || 0);
      gl.uniform3f(
        this.uniforms.uColor,
        this.color[0],
        this.color[1],
        this.color[2]
      );
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    animate({ from, to, duration, ease, onDone }) {
      if (!this.gl) {
        onDone?.();
        return;
      }
      if (this.raf) cancelAnimationFrame(this.raf);
      this.animating = true;
      this.show();
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        this.progress = from + (to - from) * ease(t);
        this.draw(now * 0.001);
        if (t < 1) {
          this.raf = requestAnimationFrame(tick);
          return;
        }
        this.progress = to;
        this.draw(now * 0.001);
        this.animating = false;
        this.raf = 0;
        onDone?.();
      };
      this.raf = requestAnimationFrame(tick);
    }

    expand(onDone) {
      this.animate({
        from: 0,
        to: MAX_PROGRESS,
        duration: DURATION_OUT,
        ease: easeInOutSmooth,
        onDone,
      });
    }

    fadeOut(onDone) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.animating = true;
      this.show();
      this.progress = MAX_PROGRESS;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / DURATION_FADE);
        this.canvas.style.opacity = String(1 - easeOutSine(t));
        this.draw(now * 0.001);
        if (t < 1) {
          this.raf = requestAnimationFrame(tick);
          return;
        }
        this.animating = false;
        this.raf = 0;
        this.reset();
        onDone?.();
      };
      this.raf = requestAnimationFrame(tick);
    }

    holdCover() {
      this.show();
      this.progress = MAX_PROGRESS;
      if (this.raf) cancelAnimationFrame(this.raf);
      const swirl = (now) => {
        this.draw(now * 0.001);
        this.canvas.style.backgroundColor = '';
        this.raf = requestAnimationFrame(swirl);
      };
      this.raf = requestAnimationFrame(swirl);
    }
  }

  function whenPageReady(callback) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      requestAnimationFrame(() => requestAnimationFrame(callback));
    };
    if (
      document.readyState === 'complete' ||
      document.body?.classList.contains('loaded')
    ) {
      finish();
      return;
    }
    document.addEventListener('page:loaded', finish, { once: true });
    window.addEventListener('load', finish, { once: true });
  }

  function isModifiedClick(event) {
    return (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  function shouldHandle(link) {
    if (!link || !link.href) return false;
    if (link.target === '_blank' || link.hasAttribute('download')) return false;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return false;
    return (
      url.pathname + url.search !==
      window.location.pathname + window.location.search
    );
  }

  function init() {
    const canvas = document.querySelector('[data-cloud-canvas]');
    if (!canvas) return;

    const cloud = new CloudTransition(canvas);
    const fallbackColor = canvas.dataset.color || DEFAULT_COLOR;
    let swapped = false;

    function playInbound() {
      const inbound = readStore();
      if (!inbound || prefersReducedMotion() || !cloud.gl) {
        clearStore();
        cloud.reset();
        return;
      }
      clearStore();
      const color = inbound.color || fallbackColor;
      cloud.setColor(color);
      cloud.canvas.style.backgroundColor = color;
      if (typeof inbound.x === 'number' && typeof inbound.y === 'number') {
        cloud.mouse = [inbound.x, inbound.y];
      }
      cloud.holdCover();
      whenPageReady(() => {
        window.setTimeout(() => cloud.fadeOut(), 60);
      });
    }

    playInbound();

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      cloud.reset();
    });

    window.addEventListener('pagehide', (event) => {
      if (event.persisted) cloud.reset();
    });

    window.addEventListener('popstate', () => {
      if (swapped) window.location.reload();
    });

    document.addEventListener(
      'pointerdown',
      (event) => {
        const link = event.target.closest(
          '[data-cloud-nav][href], a.twm__btn[href]'
        );
        if (!link || !shouldHandle(link)) return;
        fetchPage(link.href).catch(() => {});
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest(
          '[data-cloud-nav][href], a.twm__btn[href]'
        );
        if (!link || isModifiedClick(event)) return;
        if (prefersReducedMotion() || !cloud.gl || cloud.animating) return;
        const inEditor = document.body.classList.contains('shopify-design-mode');
        if (!inEditor && !shouldHandle(link)) return;

        event.preventDefault();
        const color = resolveLinkColor(link, fallbackColor);
        cloud.setColor(color);
        cloud.setMouse(event.clientX, event.clientY);

        let finished = false;
        const fallbackGo = () => {
          if (finished) return;
          finished = true;
          writeStore({
            x: cloud.mouse[0],
            y: cloud.mouse[1],
            color: colorToHex(color),
          });
          window.location.assign(link.href);
        };

        if (inEditor) {
          cloud.expand(() => fallbackGo());
          return;
        }

        const htmlReady = Promise.race([
          fetchPage(link.href),
          new Promise((_, reject) => {
            window.setTimeout(
              () => reject(new Error('timeout')),
              FETCH_TIMEOUT
            );
          }),
        ]);

        cloud.expand(() => {
          if (finished) return;
          cloud.holdCover();
          htmlReady
            .then((html) => {
              if (finished) return;
              applyPage(html, link.href);
              finished = true;
              swapped = true;
              clearStore();
              window.setTimeout(() => {
                cloud.fadeOut(() => jumpToHash(link.href));
              }, 60);
            })
            .catch(fallbackGo);
        });
      },
      true
    );
  }

  init();
})();
